import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import DOMPurify from 'dompurify';

import { INote, IParentEntity } from '../interfaces/entity';
import { INoteList, Note, NoteType } from '../models/note';
import { SubjectNote } from '../models/subject-note';
import { TaskNote } from '../models/task-note';
import { BaseRestService } from './base-rest.service';
import { SubjectNoteService } from './subject-note.service';
import { FuseUtils } from '@fuse/utils';
import { TaskNoteService } from './task-note.service';
import { map, take } from 'rxjs/operators';
import { SubjectService } from './subject.service';
import { SubjectTypeService } from './subject-type.service';
import { forkJoin, of } from 'rxjs';
import { UserService } from './user.service';
import { User } from '../models/user';
import { PrintService } from './print.service';
import { PrintHtmlService } from './print-html.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class NoteService
  extends BaseRestService<Note, INoteList>
  implements OnDestroy
{
  public static filters = [
    {
      id: 0,
      handle: 'important',
      title: 'Important',
      icon: 'error',
    },
    {
      id: 1,
      handle: 'sensitive',
      title: 'Sensitive',
      icon: 'lock',
    },
    {
      id: 2,
      handle: 'starred',
      title: 'Starred',
      icon: 'star',
    },
  ];

  public static types = [
    {
      id: 0,
      handle: 'all',
      title: 'All',
      icon: 'merge_type',
    },
    {
      id: 1,
      handle: 'draft',
      title: 'Draft',
      icon: 'drafts',
    },
    {
      id: 2,
      handle: 'completed',
      title: 'Locked',
      icon: 'party_mode',
    },
  ];

  notes: INote[];
  selectedNotes: INote[];
  currentNote: INote;
  searchText = '';
  noteOrderAscending = false;
  noteTypeFilters: NoteType[] = [];

  types: any[];
  filters: any[];
  labels: any[];
  routeParams: any;

  onNotesChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  onSelectedNotesChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  onCurrentNoteChanged: BehaviorSubject<any> = new BehaviorSubject([]);

  onTypesChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  onFiltersChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  onSearchTextChanged: BehaviorSubject<any> = new BehaviorSubject('');
  onParentChanged: BehaviorSubject<IParentEntity> = new BehaviorSubject({
    id: -1,
    uid: '',
    type: '',
    summary: '',
  });

  searchTextChanged: Subscription;

  constructor(
    http: HttpClient,
    private subjectNoteService: SubjectNoteService,
    private taskNoteService: TaskNoteService,
    private subjectService: SubjectService,
    private subjectTypeService: SubjectTypeService,
    private userService: UserService,
    private printService: PrintService,
    private printHtmlService: PrintHtmlService,
    protected router: Router
  ) {
    super(NoteService, http, 'api/notes', 'note', router);
    this.selectedNotes = [];
  }

  ngOnDestroy(): void {}

  /**
   * Get all note types
   * @returns {Promise<any>}
   */
  getTypes(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.types = NoteService.types;
      this.onTypesChanged.next(this.types);
      resolve(this.types);
    });
  }

  /**
   * Get all filters
   * @returns {Promise<any>}
   */
  getFilters(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.filters = NoteService.filters;
      this.onFiltersChanged.next(this.filters);
      resolve(this.filters);
    });
  }

  // /**
  //  * Get all labels
  //  * @returns {Promise<any>}
  //  */
  // getLabels(): Promise<any>
  // {
  //     return new Promise((resolve, reject) => {
  //         this.labels = NoteService.labels;
  //         this.onLabelsChanged.next(this.labels);
  //         resolve(this.labels);
  //     });
  // }

  /**
   * Resolve
   * @param {ActivatedRouteSnapshot} route
   * @param {RouterStateSnapshot} state
   * @returns {Observable<any> | Promise<any> | any}
   */
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> | Promise<any> | any {
    this.routeParams = route.params;

    return new Promise<void>((resolve, reject) => {
      Promise.all([
        this.getTypes(),
        this.getFilters(),
        // this.getLabels(),
        this.getNotes({
          id: -1,
          type: this.routeParams.parentType,
          uid: this.routeParams.parentId,
          summary: '',
        }),
      ]).then(() => {
        this.setCurrentNote(this.routeParams.noteId);

        // Remove all selected notes when changing Note Type Selection.
        this.deselectNotes();

        if (!this.searchTextChanged) {
          this.searchTextChanged = this.onSearchTextChanged.subscribe(
            (searchText) => {
              if (searchText !== '') {
                this.searchText = searchText;
                this.getNotes({
                  id: -1,
                  type: this.routeParams.parentType,
                  uid: this.routeParams.parentId,
                  summary: '',
                });
              } else {
                this.searchText = searchText;
                this.getNotes({
                  id: -1,
                  type: this.routeParams.parentType,
                  uid: this.routeParams.parentId,
                  summary: '',
                });
              }
            }
          );
        }
        resolve();
      }, reject);
    });
  }

  get hasPrevious() {
    return this.getCurrentNoteIndex() - 1 > -1;
  }

  get hasNext() {
    return this.getCurrentNoteIndex() + 1 < this.notes.length;
  }

  selectPrevious() {
    this.setCurrentNoteByIndex(this.getCurrentNoteIndex() - 1);
  }

  selectNext() {
    this.setCurrentNoteByIndex(this.getCurrentNoteIndex() + 1);
  }

  private getCurrentNoteIndex(): number {
    let index = 0;
    if (this.currentNote) {
      index = this.notes.findIndex((x) => x.uid === this.currentNote.uid);
      if (index < 0) {
        index = 0;
      }
    }
    return index;
  }

  private setCurrentNoteByIndex(index: number) {
    if (index < 0) {
      index = 0;
    }
    if (index > this.notes.length - 1) {
      index = this.notes.length - 1;
    }
    this.setCurrentNote(this.notes[index].uid);
  }

  /**
   * Toggle selected note by id
   * @param id
   */
  toggleSelectedNote(id: number) {
    if (
      this.selectedNotes.length > 0 &&
      this.selectedNotes.find((x) => x.note.id === id)
    ) {
      // note already selected, remove from selectedNotes
      this.selectedNotes = this.selectedNotes.filter((x) => x.note.id !== id);
    } else {
      // note not in selectedNotes, add
      const note = this.notes.find((x) => x.note.id === id);
      if (note) {
        this.selectedNotes.push(note);
      }
    }

    // notify changes
    this.onSelectedNotesChanged.next(this.selectedNotes);
  }

  /**
   * Toggle select all
   */
  toggleSelectAll() {
    if (this.selectedNotes.length > 0) {
      this.deselectNotes();
    } else {
      this.selectNotes();
    }
  }

  selectNotes(filterParameter?, filterValue?) {
    this.selectedNotes = [];

    // If there is no filter, select all notes
    if (filterParameter === undefined || filterValue === undefined) {
      this.selectedNotes = this.notes;
    } else {
      this.selectedNotes.push(
        ...this.notes.filter((note) => {
          return note.note[filterParameter] === filterValue;
        })
      );
    }

    this.selectedNotes = this.selectedNotes.filter((x) => x !== undefined);

    // Notify changes
    this.onSelectedNotesChanged.next(this.selectedNotes);
  }

  deselectNotes() {
    this.selectedNotes = [];

    // Notify changes
    this.onSelectedNotesChanged.next(this.selectedNotes);
  }

  /**
   * Set current note by uid
   * @param uid
   */
  setCurrentNote(uid?: string, notifyListeners: boolean = true) {
    this.currentNote = uid ? this.notes.find((note) => note.uid === uid) : null;
    if (notifyListeners) {
      this.onCurrentNoteChanged.next(this.currentNote);
    }
  }

  toggleSensitiveOnSelectedNotes() {
    const save$ = [];
    this.selectedNotes.map((note) => {
      note.note.toggleSensitive();
      save$.push(this.saveNoteAsObservable(note));
    });
    return save$.length > 0 ? forkJoin(save$) : of(null);
  }

  toggleStarredOnSelectedNotes() {
    const save$ = [];
    this.selectedNotes.map((note) => {
      note.note.toggleStar();
      save$.push(this.saveNoteAsObservable(note));
    });
    return save$.length > 0 ? forkJoin(save$) : of(null);
  }

  toggleImportantOnSelectedNotes() {
    const save$ = [];
    this.selectedNotes.map((note) => {
      note.note.toggleImportant();
      save$.push(this.saveNoteAsObservable(note));
    });
    return save$.length > 0 ? forkJoin(save$) : of(null);
  }

  toggleNotesOrder(parent: IParentEntity) {
    this.noteOrderAscending = !this.noteOrderAscending;
    this.setNotes(parent, this.notes);
  }

  setNoteTypeFilter(noteTypeFilter: NoteType[]) {
    this.noteTypeFilters = noteTypeFilter || [];
  }

  ApplyNoteTypeFilter(notes: INote[]): INote[] {
    // No filters selected, return nothing
    if (
      !this.noteTypeFilters ||
      (this.noteTypeFilters && this.noteTypeFilters.length == 0)
    )
      return [];

    // 'All' noteTypes - return all notes
    if (
      this.noteTypeFilters &&
      this.noteTypeFilters.filter((i) => i.name == 'All' && i.id == -1)
        .length == 1
    )
      return notes;

    // Filter notes
    var filterNotes: INote[] = [];
    notes.forEach((i) => {
      // Always include notes that have hidden note types.
      if (i.note.noteType.hideRecord == true) filterNotes.push(i);
      else {
        if (this.noteTypeFilters.find((j) => j.id == i.note.noteTypeId)) {
          filterNotes.push(i);
        }
      }
    });
    return filterNotes;
  }

  deleteSelectedNotes(parent: IParentEntity) {
    const delete$ = [];
    this.selectedNotes.forEach((note) => {
      if (!note.sequenceId) {
        delete$.push(this.deleteNote(parent, note).pipe(take(1)));
      }
    });

    if (delete$.length > 0) {
      forkJoin(delete$)
        .pipe(take(1))
        .subscribe((_) => {
          this.deselectNotes();
          this.setCurrentNote();
          this.getNotesForParent(parent)
            .pipe(take(1))
            .subscribe((notes) => {
              this.setNotes(parent, notes);
            });
        });
    }
  }

  private getNoteService(parent: IParentEntity) {
    switch (parent.type?.toLowerCase()) {
      case 'task':
        return this.taskNoteService;
      default:
        return this.subjectNoteService;
    }
  }

  private updateNotes(note: INote) {
    if (!this.notes) return;
    const index = this.notes.findIndex((x) => x.uid === note.uid);
    if (index > -1) {
      // item alredy exists, splice in the update
      this.notes.splice(index, 1, note);
    } else {
      // item doesn't exist, push it on to the end
      this.notes.push(note);
    }
  }

  refreshNotes() {
    this.onNotesChanged.next(this.notes);
  }

  private processSavedNote(note: INote, parent: IParentEntity) {
    if (note) {
      note.note = new Note(note.note);
      this.updateNotes(note);
      this.setNotes(parent, this.notes);
      this.setCurrentNote(note.uid);
    } else {
      this.setNotes(parent, this.notes);
    }
  }

  saveNoteAsObservable(
    note: INote | Note,
    successMessage?: string
  ): Observable<Note> {
    const justNote = (note as INote).note || (note as Note);
    justNote.detail = DOMPurify.sanitize(justNote.detail);
    const save$ = justNote.id
      ? this.put(justNote, successMessage)
      : this.post(justNote, successMessage);
    return save$.pipe(
      map((n) => {
        return n;
      })
    );
  }

  saveNote(
    note: INote | Note,
    parent?: IParentEntity,
    draft?: boolean,
    successMessage?: string
  ) {
    const justNote = (note as INote).note || (note as Note);
    justNote.detail = DOMPurify.sanitize(justNote.detail);
    justNote.user = null;
    if (!parent && justNote.id) {
      // we are updating the note, call super to update the note
      this.onAfterSave.pipe(take(1)).subscribe((savedNote) => {
        const currentNote = this.notes.find((i) => i.note.id === savedNote.id);
        if (currentNote) {
          currentNote.note = new Note(savedNote);
          this.setCurrentNote(currentNote.uid);
        }
      });
      this.save(justNote, successMessage);
    } else {
      if (parent) {
        const seqId = (note as INote).sequenceId;
        if (!draft && (!seqId || seqId < 1)) {
          // MRD - set the sequenceId to a negative value to indicate to the api that a proper sequenceId should be assigned
          // I know this is a little clunky but I couldn't think of a better way to do it without duplicating a lot of code from the base rest service
          // this way keeps things simple, and neat
          (note as INote).sequenceId = -1;
        }
        const noteService = this.getNoteService(parent);
        if (noteService) {
          noteService.onAfterSave.pipe(take(1)).subscribe((n) => {
            this.processSavedNote(n, parent);
            this.onAfterSave.next(n);
          });
          noteService.save(note, successMessage);
        }
      } else {
        // TODO: no parent, and no note id - is this even possible?
      }
    }
  }

  hydrate(item: Note): Observable<Note> {
    const hydrate$ = [];
    item.detail = DOMPurify.sanitize(item.detail);
    if (item.createdById && (!item.user || item.user.id !== item.createdById)) {
      hydrate$.push(
        this.userService.getById(item.createdById).pipe(
          take(1),
          map((u) => {
            item.user = new User(u);
            return item;
          })
        )
      );
    }
    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(
        take(1),
        map((_) => {
          return new Note(item);
        })
      );
    } else {
      return of(new Note(item));
    }
  }

  // delete note
  deleteNote(parent: IParentEntity, note: INote): Observable<INote> {
    let delete$: Observable<any> = of(null);
    switch (parent.type.toLowerCase()) {
      case 'task':
        delete$ = this.taskNoteService.delete(note as TaskNote);
        break;
      default:
        delete$ = this.subjectNoteService.delete(note as SubjectNote);
        break;
    }
    return delete$.pipe(
      map(() => {
        this.notes = this.notes.filter((x) => x.id !== note.id);
        this.setNotes(parent, this.notes);
        if (this.currentNote.id === note.id) {
          this.setCurrentNote(null);
        }
        return note;
      })
    );
  }

  getNotes(parent: IParentEntity): Promise<void> {
    if (this.routeParams && this.routeParams.filterHandle) {
      return this.getNotesByFilter(parent, this.routeParams.filterHandle);
    }
    return this.getNotesByType(
      parent,
      this.routeParams?.noteTypeHandle || 'all'
    );
  }

  getNotesForParent(parent: IParentEntity, type?: string): Observable<INote[]> {
    // TODO:  we don't really want all notes - we should move this into the respective type and filter getters
    if (parent && parent.type.toLowerCase() !== 'task' && parent.uid != '') {
      const noteService = this.getNoteService(parent);
      return (noteService.getNotes(parent.uid, type) as Observable<any>).pipe(
        map((notes: INote[]) => {
          return notes === undefined ? [] : notes;
        })
      );
    } else {
      const notes: INote[] = [];
      return of(notes);
    }
  }

  getNotesByType(parent: IParentEntity, handle: string): Promise<void> {
    const request$ = this.getNotesForParent(parent).pipe(
      map((notes: INote[]) => {
        let filteredNotes: INote[];
        switch (handle.toLowerCase()) {
          case 'draft':
            filteredNotes = notes.filter((note) => !note.sequenceId);
            break;
          case 'completed':
            filteredNotes = notes.filter((note) => note.sequenceId);
            break;
          default:
            filteredNotes = notes;
            break;
        }
        if (filteredNotes === undefined) {
          filteredNotes = [];
        }
        this.setNotes(parent, filteredNotes);
      })
    );
    return request$.toPromise();
  }

  getNotesByFilter(parent: IParentEntity, handle: string): Promise<void> {
    const request$ = this.getNotesForParent(parent).pipe(
      map((notes: INote[]) => {
        const filteredNotes = notes.filter(
          (note) => note.note[handle] === true
        );
        this.setNotes(parent, filteredNotes);
      })
    );
    return request$.toPromise();
  }

  sortNotes(notes: INote[], noteOrderAscending?: boolean) {
    noteOrderAscending = noteOrderAscending || this.noteOrderAscending;
    if (noteOrderAscending) {
      notes.sort((a: INote, b: INote) => {
        return (
          this.getTime(new Date(a.note.dateCreated)) -
            this.getTime(new Date(b.note.dateCreated)) || a.note.id - b.note.id
        );
      });
    } else {
      notes.sort((a: INote, b: INote) => {
        return (
          this.getTime(new Date(b.note.dateCreated)) -
            this.getTime(new Date(a.note.dateCreated)) || b.note.id - a.note.id
        );
      });
    }
    return notes;
  }

  private getTime(date?: Date) {
    return date != null ? date.getTime() : 0;
  }

  setNotes(parent: IParentEntity, notes: INote[]) {
    // expand the note object
    if (notes && notes.length > 0) {
      this.notes = notes.map((note) => {
        note.note = new Note(note.note);
        return note;
      });
    } else {
      this.notes = [];
    }

    // apply any search term...
    this.notes = FuseUtils.filterArrayByString(this.notes, this.searchText);

    // and apply order...
    this.notes = this.sortNotes(this.notes);

    // ...and notify subscribers of the change
    this.onNotesChanged.next(this.notes);

    if (parent) {
      let get$;
      if (parent.id < 1 && parent.uid) {
        switch (parent.type.toLowerCase()) {
          case 'task':
            get$ = of(parent);
            break;
          default:
            get$ = this.subjectService.get(parent.uid);
        }
      } else {
        get$ = of(parent);
      }
      get$.pipe(take(1)).subscribe((val) => {
        const summary = val.summary || '';
        if (parent.id !== val.id || parent.summary !== summary) {
          // set the id and summary
          parent.id = val.id;
          parent.summary = summary;
          if (parent.type.toLowerCase() !== 'task') {
            if (!parent.subjectType) {
              if (val.subjectTypeId) {
                parent.subjectType = this.subjectTypeService.getName(
                  val.subjectTypeId
                );
              }
            }
            parent.subjectType = parent.subjectType || 'Case';
          }
          switch (parent.type.toLowerCase()) {
            case 'referral':
              parent.url = `/referrals/referrals/subject/${parent.uid}`;
              break;
            case 'task':
              break;
            default:
              parent.url = `/subjects/${parent.subjectType}/${parent.uid}`;
          }
          // clear the selected notes
          this.selectedNotes = [];

          // ...and notify subscribers of potential parent change
          this.onParentChanged.next(parent);
        }
      });
    }
  }

  printSelectedNotes(parent: IParentEntity) {
    this.print(parent, this.selectedNotes);
  }

  print(parent: IParentEntity, notes: INote[]) {
    const noteType =
      (parent.subjectType ? parent.subjectType + ' notes' : 'Notes') +
      (parent.summary ? ' for ' + parent.summary : '');
    this.printService.printHtml(
      noteType,
      this.printHtmlService.getNotes(parent, notes, true),
      this.printHtmlService.cardStyle,
      false
    );
  }
}
